import React, { useEffect, useMemo } from 'react';

import {
  ScreenAllFilters,
  ScreenFilters,
  ScreenFiltersApply,
} from 'components/Filters';

import {
  CollapsibleBlock,
  Typography,
  DescriptionList,
  DescriptionItem,
  Grid,
  Tags,
} from '@gogame/components';
import { View, Divider, Paper, Stack } from '@gogame/components';
import { withScreen, useParams, useScreenTitle, Screen } from '@gogame/screens';

import { PreviewFile } from 'scenes/Gallery/components/File';
import {
  AttributeList,
  AttributeItem,
} from 'scenes/Gallery/components/AttributeList';

import AllFilters from 'scenes/Gallery/ConversionPages/Filters/AllFilters';
import PrimaryFilters from 'scenes/Gallery/ConversionPages/Filters/PrimaryFilters';
import screenReducer from 'scenes/Gallery/ConversionPages/screenReducer';
import { EMPTY_FILTERS } from 'scenes/Gallery/ConversionPages/schemas';

import { useLoadConversionGroupDetails } from 'scenes/Gallery/ConversionPages/ConversionGroups/data';
import {
  ConversionPagesDataTable,
  ConversionPagesDataTableProvider,
} from 'scenes/Gallery/ConversionPages/ConversionPages/ConversionPagesDataTable';

const ConversionGroupPreviewScreen = withScreen({
  name: 'conversion-group-preview',
  reducer: screenReducer,
})(() => {
  const params = useParams();
  const [, setTitle] = useScreenTitle();

  const groupId = useMemo(() => {
    if (params.groupId) {
      return Number(params.groupId);
    }
  }, [params.groupId]);

  const { data, isLoading, isRefetching } = useLoadConversionGroupDetails({
    id: groupId,
    isOptimisticResetIsNew: true,
  });

  const { name, preview, conversionPageCount, isNew } = data || {};

  useEffect(() => {
    if (!name) {
      return;
    }
    setTitle(`Group «${name}»`);
  }, [name]);

  return (
    <React.Fragment>
      <Screen.Toolbar />
      <Screen.Body>
        <ScreenFilters emptyValues={EMPTY_FILTERS}>
          <PrimaryFilters />
          <ScreenAllFilters sidePage={AllFilters} />
          <ScreenFiltersApply />
        </ScreenFilters>
        <Stack spacing={2} flex={'1 0 auto'}>
          <CollapsibleBlock
            showLabel={'Show concept details'}
            hideLabel={'Hide concept details'}
          >
            <Stack>
              <Paper variant={'outlined'} data-cy={'conceptDetails'}>
                <View loading={isLoading} fetching={isRefetching}>
                  {data ? (
                    <Stack
                      direction={'row'}
                      alignItems={'center'}
                      justifyContent={'space-between'}
                      spacing={3}
                      pt={2}
                      pl={2}
                      pr={2}
                      pb={1}
                      zeroMinWidth={true}
                    >
                      <PreviewFile
                        src={preview}
                        width={109}
                        height={64}
                        heightCover={true}
                      />
                      <Stack flex={'1 1 auto'} zeroMinWidth={true}>
                        <Typography variant={'caption'} color={'textSecondary'}>
                          {`Group #${groupId}`}
                        </Typography>
                        <Typography variant={'h6'} noWrap={true}>
                          {name}
                        </Typography>
                        <Stack spacing={1} direction={'row'}>
                          <AttributeList type={'text'}>
                            <AttributeItem
                              valueParser={'conversionPagesCount'}
                              value={conversionPageCount}
                            />
                          </AttributeList>
                          <AttributeList type={'icon'}>
                            {isNew ? (
                              <AttributeItem valueParser={'new'} />
                            ) : null}
                          </AttributeList>
                        </Stack>
                      </Stack>
                    </Stack>
                  ) : null}
                  <Divider />
                  <Grid container spacing={3} p={2}>
                    <Grid item xs={6}>
                      <DescriptionList
                        data={data}
                        stripEmpty={true}
                        disableGutters={true}
                        termWidth={'180px'}
                      >
                        <DescriptionItem
                          label={'Application'}
                          field={'application.name'}
                          type={'text'}
                        />
                        <DescriptionItem
                          label={'Domain'}
                          field={'domain'}
                          type={'text'}
                        />
                      </DescriptionList>
                    </Grid>
                    <Grid item xs={6}>
                      <DescriptionList
                        data={data}
                        stripEmpty={true}
                        disableGutters={true}
                        termWidth={'180px'}
                      >
                        <DescriptionItem
                          label={'Flow'}
                          field={'conversionPageFlow.name'}
                          type={'text'}
                        />
                        <DescriptionItem
                          label={'Tags'}
                          field={'tags'}
                          type={({ value }) => (
                            <Tags
                              data={value}
                              getTagLabel={({ name }) => name}
                              lineClamp={4}
                            />
                          )}
                        />
                      </DescriptionList>
                    </Grid>
                  </Grid>
                </View>
              </Paper>
            </Stack>
          </CollapsibleBlock>
          <Stack flex={'1 0 auto'}>
            <ConversionPagesDataTableProvider>
              <ConversionPagesDataTable height={700} withTitle={true} />
            </ConversionPagesDataTableProvider>
          </Stack>
        </Stack>
      </Screen.Body>
    </React.Fragment>
  );
});

export default ConversionGroupPreviewScreen;
